<template>
    <div class="bulk-section" v-if="selectedInvoices.length > 0 && !isTeamMember">
        <div>
            <span>{{ selectedInvoices.length }}</span>
            invoice{{ selectedInvoices.length > 1 ? 's' : '' }} selected.
        </div>
        <div>
            <payment-status-button
                :is-bulk="true"
                @invoiceBulkStatusUpdate="handleBulkChangeStatus"
                :bulk-team-member-invoice-ids="selectedInvoiceIds"
            />
            <md-button
                v-if="hasAccountingPackage"
                class="md-round md-just-icon md-warning"
                title="Send to Accounting Package"
                @click="handleSendToAccountingPackage"
            >
                <md-icon>send</md-icon>
            </md-button>
            <approve-invoice-button
                v-if="!isReadOnlyUser"
                :is-bulk="true"
                :bulk-team-member-invoice-ids="draftInvoiceIds"
                @invoiceBulkApprove="handleBulkApprove"
            />
            <send-email-button
                v-if="!isReadOnlyUser"
                :is-bulk="true"
                :bulk-team-member-invoice-ids="selectedInvoiceIds"
                @handleBulkSent="handleBulkSentEmail"
            />
            <delete-invoice-button
                :is-bulk="true"
                :bulk-team-member-invoice-ids="selectedInvoiceIds"
                @invoiceBulkDelete="handleBulkDelete"
            />
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage } from '@/helpers';
import { mapGetters } from 'vuex';
import { FEATURE_NAMES } from '@/utils/constants';
import {
    ApproveInvoiceButton,
    SendEmailButton,
    PaymentStatusButton,
    DeleteInvoiceButton
} from '../../TeamMemberInvoices/buttons';

export default {
    name: 'BulkActions',
    components: {
        ApproveInvoiceButton,
        SendEmailButton,
        PaymentStatusButton,
        DeleteInvoiceButton
    },
    props: {
        selectedInvoices: {
            type: Array,
            required: true
        },
        triggerSyncToAccountingPackage: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            FEATURE_NAMES
        };
    },
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser',
            hasAccountingPackage: 'generalSetting/hasAccountingPackage',
            isTeamMember: 'user/isTeamMember'
        }),
        releaseInvoiceIds() {
            return this.selectedInvoices
                .filter((invoice) => invoice.status !== 'Draft')
                .map((invoice) => invoice.teamMemberInvoiceId);
        },
        selectedInvoiceIds() {
            return this.selectedInvoices.map((invoice) => invoice.teamMemberInvoiceId);
        },
        draftInvoiceIds() {
            // Only Draft invoices can be approved (excludes OnHold)
            return this.selectedInvoices
                .filter((invoice) => invoice.status === 'Draft')
                .map((invoice) => invoice.teamMemberInvoiceId);
        }
    },
    methods: {
        async handleSendToAccountingPackage() {
            if (this.releaseInvoiceIds.length === 0) {
                showErrorMessage(this, 'No invoices available to send to accounting package');
                return;
            }

            this.$messageBox
                .show({
                    class: 'sm-modal-container',
                    title: 'Send to Accounting Package',
                    body: 'Are you sure you want to send the selected invoices to the accounting package?',
                    buttons: ['Confirm', 'Cancel']
                })
                .then(async (response) => {
                    if (response.toLowerCase() === 'confirm') {
                        try {
                            this.triggerSyncToAccountingPackage(this.releaseInvoiceIds);
                            const endpoint = `/api/team-member-invoices/send-to-accounting`;
                            const data = {
                                invoiceIdList: this.releaseInvoiceIds
                            };
                            const payload = {
                                method: 'post',
                                data
                            };
                            await handleRequests(endpoint, payload);
                            this.$notifySuccess('Invoices sent to accounting package successfully');
                        } catch (error) {
                            const message = 'Error in sending the invoices to the accounting package';
                            showErrorMessage(this, message, error);
                        }
                    }
                });
        },
        handleBulkApprove(result) {
            if (result) {
                this.$notifySuccess('Invoices approved successfully');
                this.$emit('refresh');
            }
        },
        handleBulkDraftSent(invoiceIdList) {
            if (invoiceIdList && invoiceIdList.length > 0) {
                this.$notifySuccess('Draft invoices sent successfully');
                this.$emit('refresh');
            }
        },
        handleBulkSentEmail(invoiceIdList) {
            if (invoiceIdList && invoiceIdList.length > 0) {
                this.$emit('refresh');
            }
        },
        handleBulkChangeStatus(response) {
            if (response) {
                this.$notifySuccess('Invoice status updated successfully');
                this.$emit('refresh');
            }
        },
        handleBulkDelete({ result, ids }) {
            if (result) {
                this.$notifySuccess('Invoices deleted successfully');
                this.$emit('refresh');
            } else {
                const message = 'There was an error deleting the invoices';
                showErrorMessage(this, message);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.bulk-section {
    max-width: 700px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    background-color: #fafafb;
    font-weight: 400;
    border-radius: 6px;
    margin: 0 auto;
    margin-bottom: 10px;

    > div:first-child {
        width: 40%;
        display: inline-block;
        vertical-align: middle;
        text-align: left;
    }

    > div:last-child {
        width: 60%;
        text-align: right;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    span {
        font-weight: 600;
    }

    .md-button {
        height: 30px;
        width: 30px;
        min-width: 30px;
        margin: 0 3px;
    }

    ::v-deep .dropdown {
        width: 180px;
        background-color: #ff9800 !important;
        display: inline-block;
        text-align: center;
        color: #fff;
        text-transform: uppercase;
        font-size: 12px;
        height: 30px;
        line-height: 30px;
        cursor: pointer;
        margin-right: 3px;
    }
}
</style>
